import React, { useState, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function CraftingTheNextHorizon() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!containerRef.current) return;
    const {
      left,
      top,
      width,
      height
    } = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    setMousePosition({
      x,
      y
    });
  };
  const handleMouseLeave = () => {
    setMousePosition({
      x: 0,
      y: 0
    });
  };
  return <section data-section-id="28130" id="strategic-vision-hero" ref={sectionRef} className="relative min-h-[80vh] w-full overflow-hidden bg-background py-20 lg:py-0 flex items-center" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <div ref={containerRef} className="container mx-auto flex flex-col lg:flex-row items-center h-full relative z-10 px-6">
        {}
        <div className="w-full lg:w-[55%] flex flex-col justify-center space-y-8 lg:pr-12 order-2 lg:order-1 mt-12 lg:mt-0">
          <div className={`transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
              <span className="text-primary block">Global Expansion,</span>
              <span className="bg-gradient-to-r from-primary to-muted-foreground bg-clip-text text-transparent block">
                Executed with Precision.
              </span>
            </h2>
          </div>

          <div className={`transition-all duration-1000 delay-200 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <p className="font-sans font-medium text-lg md:text-xl text-muted-foreground max-w-xl">
              Leveraging a century of executive leadership to navigate global complexity. We build resilient supply chains and iconic brands, matching elite demand with visionary entrepreneurs.
            </p>
          </div>

          <div className={`transition-all duration-1000 delay-400 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="relative inline-block group">
              {}
              <div className="absolute -inset-1 bg-primary/10 rounded-sm blur-sm opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
              <Button asChild size="lg" className="relative h-14 px-10 text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/95 border-0 rounded-sm shadow-xl shadow-primary/5 tracking-wider">
                <Link to="/TheCarefulConsultingPage">
                  Initiate Strategy
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="w-full lg:w-[45%] flex items-center justify-center relative order-1 lg:order-2 h-[400px] lg:h-[600px]">
          <div className={`relative w-full h-full flex items-center justify-center transition-opacity duration-1000 delay-300 ${isVisible ? "opacity-100" : "opacity-0"}`} style={{
          perspective: "1000px"
        }}>
            {}
            <div className="relative w-full max-w-[500px] aspect-square transition-transform duration-200 ease-out will-change-transform" style={{
            transform: `rotateY(${mousePosition.x * 15}deg) rotateX(${mousePosition.y * -15}deg) translateZ(20px)`
          }}>
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582219/img/abstract_3d_sculpture_cyan_indigo.png" alt="The Careful Consulting TCC Logo showing deep debossed legacy" className="w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.1)] opacity-90 scale-110 lg:scale-125 translate-x-12" />

              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary/10 blur-[120px] rounded-full -z-10 pointer-events-none mix-blend-multiply" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-primary/5 blur-[100px] rounded-full -z-10 pointer-events-none mix-blend-multiply" />
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10 opacity-30">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-muted/5 rounded-full blur-[120px]" />
      </div>
    </section>;
}